import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  TextInput,
  ActionList,
  IconButton,
  Flash,
  Dialog,
  Text,
} from "@primer/react";
import {
  AlertIcon,
  FileBadgeIcon,
  SearchIcon,
  TrashIcon,
} from "@primer/octicons-react";
import { khoanNopGroupApi } from "../../../api/sf_khoannop_group_api";
import { saleSetRegisterDichVuApi } from "../../../api/sf_saleset_register_dichvu_api";
import { ComboboxNamHoc } from "../../../components/combobox-namhoc";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { NotifyHelper } from "../../../helpers/toast";
import { KhoanNopByHocSinhItemResponse } from "../../../models/response/dich-vu/KhoanNopByHocSinhItemResponse";

interface DangKyDichVuProps {
  hocSinh: any;
}

const DangKyDichVu: React.FC<DangKyDichVuProps> = ({ hocSinh }) => {
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  const [namHocSelected, setNamHocSelected] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const [khoanNopGroups, setKhoanNopGroups] = useState<
    KhoanNopByHocSinhItemResponse[]
  >([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isShowDeleteConfirm, setIsShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  useEffect(() => {
    setNamHocSelected(nam_hoc);
  }, [nam_hoc]);

  useEffect(() => {
    if (namHocSelected && hocSinh.id) {
      loadKhoanNopGroups();
    }
  }, [namHocSelected, hocSinh.id]);

  const loadKhoanNopGroups = async () => {
    setIsLoading(true);
    try {
      const res = await khoanNopGroupApi.SelectByHocSinh({
        ts_hocsinh_id: hocSinh.id,
        nam_hoc: namHocSelected,
        dm_coso_id,
      });

      if (res.is_success) {
        setKhoanNopGroups(res.data);
        setSelectedIds(
          res.data
            .filter((item: KhoanNopByHocSinhItemResponse) => item.is_checked)
            .map(
              (item: KhoanNopByHocSinhItemResponse) => item.sf_khoannop_group_id
            )
        );
      } else {
        NotifyHelper.Error("Có lỗi");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    } finally {
      setIsLoading(false);
    }
  };
  const handleCheckboxChange = async (id: number, checked: boolean) => {
    try {
      const requestData = {
        id: 0,
        ts_hocsinh_id: hocSinh.id,
        nam_hoc: namHocSelected,
        sf_khoannop_group_id: 0,
        list_khoannop_group: [{
          sf_khoannop_group_id: id,
          is_checked: checked
        }]
      };
  
      const res = await saleSetRegisterDichVuApi.InsertMultiple(requestData);
  
      if (res.is_success) {
        setSelectedIds(prevIds =>
          checked 
            ? [...prevIds, id]
            : prevIds.filter(prevId => prevId !== id)
        );
  
        setKhoanNopGroups(prevGroups =>
          prevGroups.map(group =>
            group.sf_khoannop_group_id === id 
              ? { ...group, is_checked: checked }
              : group
          )
        );
        NotifyHelper.Success("Cập nhật thành công");
      } else {
        NotifyHelper.Error("Có lỗi");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    }
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const allGroups = khoanNopGroups.map((group) => ({
        sf_khoannop_group_id: group.sf_khoannop_group_id,
        is_checked: selectedIds.includes(group.sf_khoannop_group_id),
      }));

      const requestData = {
        id: 0,
        ts_hocsinh_id: hocSinh.id,
        nam_hoc: namHocSelected,
        sf_khoannop_group_id: 0,
        list_khoannop_group: allGroups,
      };

      const res = await saleSetRegisterDichVuApi.InsertMultiple(requestData);

      if (res.is_success) {
        NotifyHelper.Success("Cập nhật thành công");
        loadKhoanNopGroups();
      } else {
        NotifyHelper.Error("Có lỗi");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveItem = async (id: number) => {
    setItemToDelete(id);
    setIsShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete !== null) {
      try {
        const requestData = {
          id: 0,
          ts_hocsinh_id: hocSinh.id,
          nam_hoc: namHocSelected,
          sf_khoannop_group_id: 0,
          list_khoannop_group: [
            {
              sf_khoannop_group_id: itemToDelete,
              is_checked: false,
            },
          ],
        };

        const res = await saleSetRegisterDichVuApi.InsertMultiple(requestData);

        if (res.is_success) {
          // Cập nhật UI
          setSelectedIds((prevIds) =>
            prevIds.filter((id) => id !== itemToDelete)
          );
          setKhoanNopGroups((prevGroups) =>
            prevGroups.map((group) =>
              group.sf_khoannop_group_id === itemToDelete
                ? { ...group, is_checked: false }
                : group
            )
          );
          NotifyHelper.Success("Xóa thành công");
        } else {
          NotifyHelper.Error("Có lỗi khi xóa");
        }
      } catch (error) {
        NotifyHelper.Error("Có lỗi khi xóa");
      }
      setIsShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  };

  const filteredKhoanNop = useMemo(() => {
    return khoanNopGroups.filter((item) =>
      item.ten_khoan_nop_group.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [khoanNopGroups, searchQuery]);

  const selectedItems = useMemo(() => {
    return khoanNopGroups.filter((item) =>
      selectedIds.includes(item.sf_khoannop_group_id)
    );
  }, [khoanNopGroups, selectedIds]);

  return (
    <Box sx={{ padding: 0 }}>
      <Box sx={{ display: "flex", gap: 3, marginBottom: 3 }}>
        <FormControl>
          <FormControl.Label>Năm học</FormControl.Label>
          <ComboboxNamHoc
            value={namHocSelected}
            onValueChanged={(value: string) => setNamHocSelected(value)}
          />
        </FormControl>

        <FormControl sx={{ flex: 1 }}>
          <FormControl.Label>Tìm kiếm</FormControl.Label>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextInput
              sx={{ flex: 1 }}
              leadingVisual={SearchIcon}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nhập để tìm kiếm..."
            />
          </Box>
        </FormControl>
      </Box>

      {error && (
        <Flash variant="danger" sx={{ mb: 3 }}>
          {error}
        </Flash>
      )}

      <Dialog
        isOpen={isShowDeleteConfirm}
        onDismiss={() => setIsShowDeleteConfirm(false)}
      >
        <Dialog.Header>
          <Box display="flex" alignItems="center">
            <div style={{ color: "orange" }}>
              <AlertIcon size={28} />
            </div>
            <Box ml={2}>Lưu ý</Box>
          </Box>
        </Dialog.Header>
        <Box p={3}>
          <Text>Bạn có chắc chắn muốn xóa dịch vụ này?</Text>
          <Box display="flex" mt={3} justifyContent="flex-end">
            <Box mr={2}>
              <Button
                variant="default"
                onClick={() => setIsShowDeleteConfirm(false)}
              >
                Đóng
              </Button>
            </Box>
            <Button variant="danger" onClick={confirmDelete}>
              Xóa
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: 2,
              maxHeight: "calc(100vh - 200px)",
              overflowY: "auto",
            }}
          >
             <Box
              sx={{
                bg: "canvas.subtle",
                p: 2,
                borderBottom: "1px solid",
                borderColor: "border.default",
                fontWeight: "bold",
                color: "fg.muted",
              }}
            >
              Danh sách dịch vụ
            </Box>
            <ActionList>
              {filteredKhoanNop.map((item) => (
                <ActionList.Item
                  key={item.sf_khoannop_group_id}
                  style={
                    selectedIds.includes(item.sf_khoannop_group_id)
                      ? { opacity: 0.5 }
                      : {}
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Checkbox
                      checked={selectedIds.includes(item.sf_khoannop_group_id)}
                      onChange={(e) =>
                        handleCheckboxChange(
                          item.sf_khoannop_group_id,
                          e.target.checked
                        )
                      }
                    />
                    <Box sx={{ flex: 1, ml: 2 }}>
                      {item.ten_khoan_nop_group}
                    </Box>
                  </Box>
                </ActionList.Item>
              ))}
            </ActionList>
          </Box>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: 2,
              maxHeight: "calc(100vh - 420px)",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                bg: "canvas.subtle",
                p: 2,
                borderBottom: "1px solid",
                borderColor: "border.default",
                fontWeight: "bold",
                color: "fg.muted",
              }}
            >
              Danh sách đã chọn
            </Box>
            <ActionList showDividers>
              {selectedItems.map((item) => (
                <ActionList.Item key={item.sf_khoannop_group_id}>
                  <ActionList.LeadingVisual>
                    <FileBadgeIcon size={16} />
                  </ActionList.LeadingVisual>
                  <Box sx={{ display: "flex", flex: 1, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: "100%",
                        pr: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.ten_khoan_nop_group}
                    </Box>
                  </Box>
                  <ActionList.TrailingVisual>
                    <IconButton
                      size="small"
                      icon={TrashIcon}
                      variant="danger"
                      aria-label="Delete"
                      onClick={() =>
                        handleRemoveItem(item.sf_khoannop_group_id)
                      }
                    />
                  </ActionList.TrailingVisual>
                </ActionList.Item>
              ))}
            </ActionList>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DangKyDichVu;
